import { QuestionCircleOutlined } from '@ant-design/icons';
import { ReactComponent as DiggingPermitIcon } from 'lib/icons/digging.svg';
import { ReactComponent as LeaseholdIcon } from 'lib/icons/leasehold.svg';
import { ReactComponent as ApprovedIcon } from 'lib/icons/status/approved.svg';
import { ReactComponent as ClosedIcon } from 'lib/icons/status/closed.svg';
import { ReactComponent as CreatedIcon } from 'lib/icons/status/created.svg';
import { ReactComponent as DeclinedIcon } from 'lib/icons/status/declined.svg';
import { ReactComponent as ProcessedIcon } from 'lib/icons/status/processed.svg';
import { ReactComponent as RegisteredIcon } from 'lib/icons/status/registered.svg';
import { ReactComponent as RequiresAdditionalInformationIcon } from 'lib/icons/status/requiresAdditionalInformation.svg';
import { ReactComponent as RevokedIcon } from 'lib/icons/status/revoked.svg';
import { ReactComponent as WorkDoneIcon } from 'lib/icons/status/workDone.svg';
import { ReactComponent as TAPlanIcon } from 'lib/icons/ta-plan.svg';
import { applySnapshot } from 'mobx-state-tree';
import {
  createDiggingPermit,
  createLeasehold,
  createTAPlan,
  updateDiggingPermit,
  updateLeasehold,
  updateTAPlan
} from 'utils/api';
import { CaseStatus, CaseStatusGroup, CaseType } from 'utils/enumerations';
import { getRoutePath } from './routeUtils';

export const getCaseTypeIcon = caseType => {
  switch (caseType) {
    case CaseType.DiggingPermit:
      return DiggingPermitIcon;
    case CaseType.Leasehold:
      return LeaseholdIcon;
    case CaseType.TrafficArrangementPlan:
      return TAPlanIcon;
    default:
      return QuestionCircleOutlined;
  }
};

export const getCaseStatusGroup = caseStatus => {
  switch (caseStatus) {
    case CaseStatus.Created:
    case CaseStatus.Registered:
    case CaseStatus.Processed:
    case CaseStatus.Approved:
    case CaseStatus.RequiresAdditionalInformation:
      return CaseStatusGroup.Ongoing;
    case CaseStatus.Closed:
    case CaseStatus.Declined:
    case CaseStatus.Revoked:
    case CaseStatus.WorkDone:
      return CaseStatusGroup.Completed;
    default:
      break;
  }
};

export const getCaseStatusesFromGroup = caseStatusGroup => {
  switch (caseStatusGroup) {
    case CaseStatusGroup.Ongoing:
      return [
        CaseStatus.Created,
        CaseStatus.Registered,
        CaseStatus.Processed,
        CaseStatus.Approved,
        CaseStatus.RequiresAdditionalInformation
      ];
    case CaseStatusGroup.Completed:
      return [CaseStatus.WorkDone, CaseStatus.Closed, CaseStatus.Declined, CaseStatus.Revoked];
    default:
      return [];
  }
};

export const getCaseStatusGroupName = (t, caseStatusGroup) => {
  switch (caseStatusGroup) {
    case CaseStatusGroup.Ongoing:
      return t('applicationStatus:Ongoing');
    case CaseStatusGroup.Completed:
      return t('applicationStatus:Completed');
    default:
      return 'unknown';
  }
};

export const getCaseStatusColor = caseStatus => {
  switch (caseStatus) {
    case CaseStatus.Created:
    case CaseStatus.Registered:
    case CaseStatus.Processed:
    case CaseStatus.Approved:
      return '#1075e0';
    case CaseStatus.Closed:
    case CaseStatus.Declined:
    case CaseStatus.Revoked:
    case CaseStatus.WorkDone:
      return '#707689';
    case CaseStatus.RequiresAdditionalInformation:
      return '#c71d1d';
    default:
      return '#1075e0';
  }
};

export const getCaseStatusIcon = caseStatus => {
  switch (caseStatus) {
    case CaseStatus.Created:
      return CreatedIcon;
    case CaseStatus.Registered:
      return RegisteredIcon;
    case CaseStatus.Processed:
      return ProcessedIcon;
    case CaseStatus.Approved:
      return ApprovedIcon;
    case CaseStatus.WorkDone:
      return WorkDoneIcon;
    case CaseStatus.Closed:
      return ClosedIcon;
    case CaseStatus.RequiresAdditionalInformation:
      return RequiresAdditionalInformationIcon;
    case CaseStatus.Declined:
      return DeclinedIcon;
    case CaseStatus.Revoked:
      return RevokedIcon;
    default:
      return QuestionCircleOutlined;
  }
};

export const getCaseTypeName = (t, caseType) => {
  switch (caseType) {
    case CaseType.DiggingPermit:
      return t('diggingPermits:DiggingPermit');
    case CaseType.Leasehold:
      return t('diggingPermits:GenericLeasehold');
    case CaseType.TrafficArrangementPlan:
      return t('diggingPermits:TAPlan');
    default:
      return null;
  }
};

export const getCaseStatusName = (t, caseStatus) => {
  switch (caseStatus) {
    case CaseStatus.New:
      return t('applicationStatus:New');
    case CaseStatus.Created:
      return t('applicationStatus:Created');
    case CaseStatus.Registered:
      return t('applicationStatus:Registered');
    case CaseStatus.Processed:
      return t('applicationStatus:Processed');
    case CaseStatus.Approved:
      return t('applicationStatus:Approved');
    case CaseStatus.WorkDone:
      return t('applicationStatus:WorkDone');
    case CaseStatus.Closed:
      return t('applicationStatus:Closed');
    case CaseStatus.RequiresAdditionalInformation:
      return t('applicationStatus:RequiresAdditionalInformation');
    case CaseStatus.Declined:
      return t('applicationStatus:Declined');
    case CaseStatus.Revoked:
      return t('applicationStatus:Revoked');
    default:
      return 'unknown';
  }
};

export const convertCaseStatusNumberToRuleName = caseStatus => {
  switch (caseStatus) {
    case CaseStatus.New:
      return 'new';
    case CaseStatus.Created:
      return 'created';
    case CaseStatus.Registered:
      return 'registered';
    case CaseStatus.Processed:
      return 'processed';
    case CaseStatus.Approved:
      return 'approved';
    case CaseStatus.WorkDone:
      return 'workDone';
    case CaseStatus.Closed:
      return 'closed';
    case CaseStatus.RequiresAdditionalInformation:
      return 'requiresAdditionalInformation';
    case CaseStatus.Declined:
      return 'declined';
    case CaseStatus.Revoked:
      return 'revoked';
    default:
      return 'unknown';
  }
};

export const openCase = (navigate, tenantId, caseType, caseNumber) => {
  navigate(`${getRoutePath(tenantId, caseType)}/${caseNumber}`);
};

export const saveCase = async (application, newStatus) => {
  application.sketchVM?.complete();
  const snapshot = JSON.parse(JSON.stringify(application));
  application.setAttachmentRemoved(false);
  let savedApplication = undefined;

  snapshot.tracker = undefined;
  if (application.caseType === CaseType.DiggingPermit) {
    if (application.status !== CaseStatus.New) {
      if (newStatus != null) snapshot.status = newStatus;
      savedApplication = await updateDiggingPermit(snapshot);
      applySnapshot(application, savedApplication);
    } else {
      snapshot.status = newStatus != null ? newStatus : CaseStatus.Created;
      savedApplication = await createDiggingPermit(snapshot);
      applySnapshot(application, savedApplication);
    }
    application.tracker.clearTrackedState();
    return savedApplication;
  } else if (application.caseType === CaseType.TrafficArrangementPlan) {
    if (application.status !== CaseStatus.New) {
      if (newStatus != null) snapshot.status = newStatus;
      savedApplication = await updateTAPlan(snapshot);
      applySnapshot(application, savedApplication);
    } else {
      snapshot.status = newStatus != null ? newStatus : CaseStatus.Created;
      savedApplication = await createTAPlan(snapshot);
      applySnapshot(application, savedApplication);
    }
    application.tracker.clearTrackedState();
    return savedApplication;
  } else if (application.caseType === CaseType.Leasehold) {
    if (application.status !== CaseStatus.New) {
      if (newStatus != null) snapshot.status = newStatus;
      savedApplication = await updateLeasehold(snapshot);
      applySnapshot(application, savedApplication);
    } else {
      snapshot.status = newStatus != null ? newStatus : CaseStatus.Created;
      savedApplication = await createLeasehold(snapshot);
      applySnapshot(application, savedApplication);
    }
    application.tracker.clearTrackedState();
    return savedApplication;
  }
};

export const getDomainCheckboxOptions = (rootStore, domainName, currentValues) => {
  const domain = rootStore.getDomainByName(domainName);
  return domain?.codedValues
    .filter(codedValue => !codedValue.inactive || currentValues?.includes(codedValue.code))
    .map(codedValue => ({ label: codedValue.description, value: codedValue.code }));
};

export const formatMobilePhoneNumber = value => {
  return value && value.length === 10
    ? `${value.substr(0, 3)}-${value.substr(3, 3)} ${value.substr(6, 2)} ${value.substr(8, 2)}`
    : value;
};
